import type { MasterMessage, ScreenContext } from '@iws/pos-companion'
import { CheckCircle2, ScanLine, XCircle } from 'lucide-react'

import { useEffect, useRef, useState } from 'react'

import { Card, CardContent, CardHeader, CardTitle, Input, PosButton, Segmented } from '@/components/ui'
import { CameraScanner } from '@/components/pos/scan/CameraScanner'
import { useBarcodeScanner } from '@/hooks/ui/useBarcodeScanner'
import { cn } from '@/lib/utils'
import { openLink } from '@/services/companion/link'
import { formatMoney } from '@/utils'

export const COMPANION_PATH = '/companion'

type Welcome = Extract<MasterMessage, { type: 'welcome' }>
type Cart = Extract<MasterMessage, { type: 'cart' }>
type Result = Extract<MasterMessage, { type: 'result' }>

const deviceName = () => {
  const ua = navigator.userAgent
  const m = /Android [\d.]+; ([^;)]+)/.exec(ua)
  return (m?.[1] ?? (/iPhone|iPad/.test(ua) ? 'iPhone' : 'Browser')).trim().slice(0, 60)
}

/** pos-web as a companion of a pos-app device (POS_COMPANION.md §3): scans into it and shows its cart. Needs no sign-in. */
export const CompanionPage = () => {
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<'idle' | 'connecting' | 'linked' | 'offline'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [master, setMaster] = useState<Welcome | null>(null)
  const [screen, setScreen] = useState<ScreenContext>('other')
  const [cart, setCart] = useState<Cart | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const [view, setView] = useState<'scan' | 'cart'>('scan')
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const link = useRef<ReturnType<typeof openLink> | null>(null)
  const seq = useRef(0)

  useEffect(() => {
    document.title = 'Companion'
    return () => link.current?.stop()
  }, [])

  const reset = (message?: string) => {
    link.current = null
    setStatus('idle')
    setMaster(null)
    setCart(null)
    setResult(null)
    setError(message ?? null)
  }

  const connect = (text = code) => {
    link.current?.stop()
    setError(null)
    setStatus('connecting')
    try {
      link.current = openLink(
        text,
        'companion',
        {
          onReady: () => setStatus('linked'),
          onOffline: () => setStatus('offline'),
          onEnded: (message) => reset(message === 'Unpaired' ? undefined : message),
          onEvent: (e) => {
            if (e.kind !== 'from_master') return
            const m = e.message
            if (m.type === 'welcome') setMaster(m)
            else if (m.type === 'context') setScreen(m.screen)
            else if (m.type === 'cart') setCart(m)
            else if (m.type === 'result') {
              setResult(m)
              navigator.vibrate?.(m.ok ? 40 : [80, 60, 80])
            } else if (m.type === 'bye')
              link.current?.stop(
                { shift_closed: 'The shift closed.', unpaired: 'Unpaired', signed_out: 'The device signed out.', denied: 'The device denied the request.' }[
                  m.reason
                ]
              )
          },
        },
        { device_name: deviceName(), platform: 'web' }
      )
    } catch (e) {
      reset(e instanceof Error ? e.message : 'Invalid code')
    }
  }

  const scan = (value: string) => {
    if (status !== 'linked' || !value.trim()) return
    link.current?.send({ type: 'scan', id: `w${Date.now()}-${seq.current++}`, code: value.trim().slice(0, 512) })
  }

  useBarcodeScanner(scan, status === 'linked' && view === 'scan')

  if (!master)
    return (
      <div className="flex h-dvh items-center justify-center bg-frame p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Use as a companion</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              On the POS device, open Companion → Pair a phone, then scan the QR code or type the code shown there. This browser must be on the same
              network.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                connect()
              }}
              className="space-y-3"
            >
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onCameraScan={(text) => {
                  setCode(text)
                  connect(text)
                }}
                placeholder="00BG-JTMJ"
                aria-label="Pairing code"
                className="text-center font-mono tracking-widest"
                autoFocus
              />
              <PosButton type="submit" size="touch" className="w-full" disabled={status === 'connecting' || code.replace(/[^0-9A-Z]/gi, '').length < 8}>
                {status === 'connecting' ? 'Waiting for the device…' : 'Connect'}
              </PosButton>
            </form>
            {error && <p className="text-sm text-error">{error}</p>}
          </CardContent>
        </Card>
      </div>
    )

  return (
    <div className="grid h-dvh grid-rows-[auto_auto_1fr] bg-page">
      <header className="flex items-center gap-3 border-b border-border bg-card px-4 py-3">
        <span className={cn('size-2 rounded-full', status === 'linked' ? 'bg-success' : 'bg-warning')} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-ink">
            {master.terminal_name} · {master.branch}
          </p>
          <p className="truncate text-sm text-text-2">{status === 'linked' ? master.staff : 'Reconnecting…'}</p>
        </div>
        <PosButton variant="ghost" onClick={() => link.current?.stop()}>
          Disconnect
        </PosButton>
      </header>
      <div className="px-4 py-3">
        <Segmented
          value={view}
          onChange={(v) => {
            setView(v)
            link.current?.send({ type: 'want', view: v })
          }}
          options={[
            { value: 'scan', label: 'Scan' },
            { value: 'cart', label: 'Cart' },
          ]}
        />
      </div>
      {view === 'scan' ? (
        <div className="grid content-start gap-4 px-4 pb-4">
          {screen !== 'sell' && <p className="rounded-md bg-warning-lighter p-3 text-sm text-ink">Open Sale on the device to add items.</p>}
          <PosButton
            size="touch"
            className="w-full"
            disabled={status !== 'linked'}
            onClick={(e) => setAnchor(anchor ? null : e.currentTarget.getBoundingClientRect())}
          >
            <ScanLine /> {anchor ? 'Stop camera' : 'Scan with the camera'}
          </PosButton>
          {anchor && <CameraScanner anchor={anchor} continuous onScan={scan} onClose={() => setAnchor(null)} />}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const input = e.currentTarget.elements.namedItem('code') as HTMLInputElement
              scan(input.value)
              input.value = ''
            }}
          >
            <Input name="code" placeholder="Type a barcode or SKU" aria-label="Barcode" disabled={status !== 'linked'} />
          </form>
          {result && (
            <div className={cn('flex items-start gap-3 rounded-md p-3', result.ok ? 'bg-success-lighter/60' : 'bg-error-lighter/60')}>
              {result.ok ? <CheckCircle2 className="size-5 text-success" /> : <XCircle className="size-5 text-error" />}
              <div className="min-w-0">
                <p className="font-medium text-ink">{result.title}</p>
                {result.detail && <p className="text-sm text-text-2">{result.detail}</p>}
              </div>
              {result.total !== undefined && <p className="ml-auto tabular-nums text-ink">{formatMoney(result.total)}</p>}
            </div>
          )}
        </div>
      ) : (
        <div className="grid min-h-0 grid-rows-[1fr_auto]">
          <div className="min-h-0 overflow-y-auto px-4">
            {!cart?.lines.length && <p className="py-8 text-center text-text-2">The cart is empty.</p>}
            {cart?.lines.map((l, i) => (
              <div key={i} className="flex items-baseline justify-between gap-4 border-b border-border-subtle py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{l.name}</p>
                  <p className="text-sm text-text-2">
                    {l.qty} × {formatMoney(l.unit_price)}
                  </p>
                </div>
                <p className="tabular-nums text-ink">{formatMoney(l.total)}</p>
              </div>
            ))}
          </div>
          {cart && (
            <footer className="grid gap-1 border-t border-border bg-card px-4 py-4">
              {cart.customer && <p className="text-text-2">{cart.customer}</p>}
              {cart.discount > 0 && (
                <div className="flex justify-between text-success-dark">
                  <span>Discount</span>
                  <span className="tabular-nums">{formatMoney(cart.discount)}</span>
                </div>
              )}
              {cart.vat > 0 && (
                <div className="flex justify-between text-text-2">
                  <span>VAT</span>
                  <span className="tabular-nums">{formatMoney(cart.vat)}</span>
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <span className="font-semibold text-ink">Total</span>
                <span className="text-heading-lg font-semibold tabular-nums text-ink">{formatMoney(cart.total)}</span>
              </div>
            </footer>
          )}
        </div>
      )}
    </div>
  )
}
